import { useEffect, useMemo, useState } from 'react';
import { mutations } from 'RestAPIs/rapini';
import {
  ActivityResponse,
  SuggestedActivitiesGroupResponse
} from 'generated/index';

type UseActivitySuggestionsParams = {
  employeeIds: string[] | undefined;
};

type UseActivitySuggestionsReturn = {
  showOtherActivities: boolean;
  setShowOtherActivities: (show: boolean) => void;
  displayOptions: ActivityResponse[];
  displaySuggestedOptions: ActivityResponse[];
  shouldShowMoreButton: boolean;
  shouldDisableFavoriteFilter: boolean;
};

export const useActivitySuggestions = ({
  employeeIds
}: UseActivitySuggestionsParams): UseActivitySuggestionsReturn => {
  const [showOtherActivities, setShowOtherActivities] = useState(false);
  const [activityGroups, setActivityGroups] = useState<
    SuggestedActivitiesGroupResponse[]
  >([]);

  const { mutate: fetchSuggestedActivities } =
    mutations.useSuggestedActivities();

  useEffect(() => {
    setShowOtherActivities(false);

    fetchSuggestedActivities(
      { employeeIds },
      {
        onSuccess: (data) => {
          setActivityGroups(data || []);
        },
        onError: () => {
          setActivityGroups([]);
        }
      }
    );
  }, [employeeIds, fetchSuggestedActivities]);

  const suggestionsGroup: SuggestedActivitiesGroupResponse | undefined =
    useMemo(
      () =>
        activityGroups.find((group) => group.activityGroupType === 'SUGGESTED'),
      [activityGroups]
    );

  const otherGroup: SuggestedActivitiesGroupResponse | undefined = useMemo(
    () => activityGroups.find((group) => group.activityGroupType === 'OTHER'),
    [activityGroups]
  );

  const suggestedActivities: ActivityResponse[] = useMemo(
    () => suggestionsGroup?.activities ?? [],
    [suggestionsGroup]
  );
  const otherActivities: ActivityResponse[] = useMemo(
    () => otherGroup?.activities ?? [],
    [otherGroup]
  );

  const activityDisplayConfig = useMemo(() => {
    const hasEmployees = employeeIds && employeeIds.length > 0;

    // Simple rule: show button if we have employees selected, OTHER activities to reveal, and haven't clicked yet
    const shouldShowMoreButton =
      hasEmployees && otherActivities.length > 0 && !showOtherActivities;

    if (shouldShowMoreButton) {
      // Showing suggestions mode - display suggested activities with "show more" button
      return {
        displaySuggestedOptions: suggestedActivities,
        displayOptions: [],
        shouldShowMoreButton: true,
        shouldDisableFavoriteFilter: false
      };
    }

    // Show all mode (after clicking "show more", when no employees selected, or when no OTHER activities exist)
    const allActivities = [...suggestedActivities, ...otherActivities];
    return {
      displaySuggestedOptions: [],
      displayOptions: allActivities,
      shouldShowMoreButton: false,
      shouldDisableFavoriteFilter: true
    };
  }, [employeeIds, showOtherActivities, suggestedActivities, otherActivities]);

  return {
    showOtherActivities,
    setShowOtherActivities,
    ...activityDisplayConfig
  };
};
